import React from "react";
import {
  Box,
  Card,
  Chip,
  Grid,
  Skeleton,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  Paper,
} from "@material-ui/core";
import {
  PersonOutline,
  SupervisorAccount,
  GroupAdd,
  Assignment,
} from "@material-ui/icons";
import { fNumber } from "src/utils/formatNumber";
import AppDistribucionPropuestas from "../estadisticas/app-distribucion-propuestas";

export default function ResumenPropuestasEmpleado({ data, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (loading) {
    return (
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} md={7}>
          <Skeleton variant="rectangular" height={isMobile ? 300 : 450} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Skeleton variant="rectangular" height={isMobile ? 300 : 450} />
        </Grid>
      </Grid>
    );
  }

  // Calcular totales por rol
  const totalVendedor =
    data?.comoVendedor?.reduce(
      (sum, item) => sum + parseInt(item.cantidad),
      0,
    ) || 0;
  const totalResponsable =
    data?.comoResponsable?.reduce(
      (sum, item) => sum + parseInt(item.cantidad),
      0,
    ) || 0;
  const totalAdicional =
    data?.comoAdicional?.reduce(
      (sum, item) => sum + parseInt(item.cantidad),
      0,
    ) || 0;

  const totalGeneral = totalVendedor + totalResponsable + totalAdicional;

  const getEstadoLabel = (estado) => {
    const labels = {
      CREADA: "Creada",
      ENVIADA: "Enviada",
      ACEPTADA: "Aceptada",
      ACEPTADO_CLIENTE: "Aceptada por Cliente",
      RECHAZADA: "Rechazada",
      CADUCADA: "Caducada",
    };
    return labels[estado] || estado;
  };

  const getEstadoColor = (estado) => {
    const colors = {
      CREADA: "default",
      ENVIADA: "info",
      ACEPTADA: "success",
      ACEPTADO_CLIENTE: "success",
      RECHAZADA: "error",
      CADUCADA: "warning",
    };
    return colors[estado] || "default";
  };

  // Función para renderizar sección de rol
  const RolSection = ({ titulo, icono, total, items, colorChip }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {icono}
          <Typography variant="subtitle2" fontWeight="bold">
            {titulo}
          </Typography>
        </Box>
        <Chip
          label={fNumber(total)}
          color={colorChip}
          size="small"
          sx={{ fontWeight: "bold", minWidth: 50 }}
        />
      </Box>

      {items && items.length > 0 ? (
        <Stack spacing={0.5}>
          {items.map((item, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                pl: isMobile ? 1 : 2,
                py: 0.5,
                bgcolor: "background.neutral",
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="textSecondary">
                {getEstadoLabel(item.estado)}
              </Typography>
              <Chip
                label={item.cantidad}
                size="small"
                color={getEstadoColor(item.estado)}
                sx={{ height: 20, fontSize: 11, minWidth: 40 }}
              />
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ pl: isMobile ? 1 : 2 }}
        >
          Sin propuestas en este rol
        </Typography>
      )}
    </Paper>
  );

  return (
    <Card sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight="bold">
            Resumen de Propuestas
          </Typography>
          <Chip
            icon={<Assignment />}
            label={`Total: ${fNumber(totalGeneral)}`}
            color="primary"
            sx={{ fontWeight: "bold" }}
          />
        </Box>
        <Divider />
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Gráfico de distribución por rol */}
        <Grid item xs={12} lg={7}>
          <Box sx={{ height: "100%" }}>
            <AppDistribucionPropuestas
              title="Distribución de Propuestas"
              subheader="Por rol en las propuestas"
              loading={false}
              data={{
                vendedor: {
                  items:
                    data?.comoVendedor?.map((item) => ({
                      estado: item.estado,
                      cantidad: parseInt(item.cantidad),
                    })) || [],
                },
                responsable: {
                  items:
                    data?.comoResponsable?.map((item) => ({
                      estado: item.estado,
                      cantidad: parseInt(item.cantidad),
                    })) || [],
                },
                adicional: {
                  items:
                    data?.comoAdicional?.map((item) => ({
                      estado: item.estado,
                      cantidad: parseInt(item.cantidad),
                    })) || [],
                },
              }}
            />
          </Box>
        </Grid>

        {/* Resumen por roles */}
        <Grid item xs={12} lg={5}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="subtitle2"
              gutterBottom
              color="textSecondary"
              fontWeight="medium"
              sx={{ mb: 2 }}
            >
              Detalle por Roles
            </Typography>

            {/* Como Vendedor */}
            <RolSection
              titulo="Como Vendedor"
              icono={
                <PersonOutline sx={{ color: "primary.main", fontSize: 20 }} />
              }
              total={totalVendedor}
              items={data?.comoVendedor}
              colorChip="primary"
            />

            {/* Como Responsable */}
            <RolSection
              titulo="Como Responsable"
              icono={
                <SupervisorAccount
                  sx={{ color: "secondary.main", fontSize: 20 }}
                />
              }
              total={totalResponsable}
              items={data?.comoResponsable}
              colorChip="secondary"
            />

            {/* Como Adicional - Solo si hay datos */}
            {totalAdicional > 0 && (
              <RolSection
                titulo="Como Adicional"
                icono={<GroupAdd sx={{ color: "info.main", fontSize: 20 }} />}
                total={totalAdicional}
                items={data?.comoAdicional}
                colorChip="default"
              />
            )}

            {/* Card de resumen total */}
            {!isMobile && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: "auto",
                  bgcolor: "primary.lighter",
                  borderRadius: 2,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Total General
                </Typography>
                <Typography variant="h4" color="primary.main" fontWeight="bold">
                  {fNumber(totalGeneral)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  propuestas
                </Typography>
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
}
